/**
 * Einstiegspunkt des Frontends.
 *
 * Der MSAL-Client wird vor dem Rendern initialisiert und die Antwort eines
 * Umleitungs-Anmeldevorgangs verarbeitet. Erst danach wird die Anwendung
 * gerendert, damit keine Komponente auf einen halb initialisierten
 * Authentifizierungszustand zugreift.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { getAuthConfig } from './lib/authConfig.js';
import { krannichDarkTheme, krannichLightTheme } from './lib/theme.js';
import { I18nProvider } from './i18n/index.js';
import { App } from './App.js';
import { ConfigurationError } from './components/ConfigurationError.js';
import './styles.css';

const container = document.getElementById('root');
if (!container) throw new Error('Das Wurzelelement "#root" wurde nicht gefunden.');
const root = createRoot(container);

function Root({ msalInstance }: { msalInstance: PublicClientApplication }) {
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  return (
    <StrictMode>
      <FluentProvider theme={prefersDark ? krannichDarkTheme : krannichLightTheme}>
        <I18nProvider>
          <MsalProvider instance={msalInstance}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </MsalProvider>
        </I18nProvider>
      </FluentProvider>
    </StrictMode>
  );
}

async function bootstrap(): Promise<void> {
  // Erst hier wird die Konfiguration geprueft – innerhalb des try/catch des
  // Aufrufers, damit ein fehlender Wert die Fehlerseite zeigt statt einer
  // weissen Seite (siehe Kommentar in authConfig.ts).
  const { msalConfig } = getAuthConfig();
  const msalInstance = new PublicClientApplication(msalConfig);
  await msalInstance.initialize();

  const response = await msalInstance.handleRedirectPromise();
  if (response?.account) {
    msalInstance.setActiveAccount(response.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) msalInstance.setActiveAccount(accounts[0] ?? null);
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
    }
  });

  root.render(<Root msalInstance={msalInstance} />);
}

bootstrap().catch((error: unknown) => {
  // Haeufigste Ursache: fehlende oder falsche VITE_*-Umgebungsvariablen.
  const message = error instanceof Error ? error.message : String(error);
  root.render(
    <StrictMode>
      <FluentProvider theme={webLightTheme}>
        <ConfigurationError message={message} />
      </FluentProvider>
    </StrictMode>,
  );
});
